import { useEffect, useRef } from "react";
import { loadHubSpotSdk } from "~/components/forms/HubSpotForm";

/**
 * C1 Transform 2026 registration form.
 *
 * Embeds the real HubSpot form (native `hbspt.forms.create`, not a hand-built
 * POST to the Forms API) so HubSpot's own bot protection, field validation,
 * and property definitions all apply for real. The form is configured in
 * HubSpot with "no style" (raw HTML) so this component owns 100% of the CSS
 * via the .transform-hs-form rules below.
 *
 * The original design split this into a 2-step wizard (contact info, then
 * event logistics). A JS layer that hid/revealed field groups on top of the
 * single native form turned out to race HubSpot's own per-field validation
 * timing in a way that was genuinely flaky (Continue could silently fail to
 * advance even seconds after every visible field validated individually) --
 * not something worth shipping on a revenue-critical lead-capture form. This
 * renders every field on one page instead, letting HubSpot's native
 * validation and submission behave exactly as it does everywhere else on the
 * site.
 *
 * A successful submission joins the waitlist and redirects to the dedicated
 * confirmation page. Keeping that state on its own route avoids an ambiguous
 * inline success state and gives the event team a stable post-submit URL.
 */

const CHECKBOX_FIELD_NAMES = new Set([
  "send_me_the_hotel_room_block_link",
  "interest_in_welcome_reception",
]);

interface TransformRegisterProps {
  portal_id?: string;
  form_id: string;
  event_date_label?: string;
  venue_name?: string;
  venue_address?: string;
  cost_label?: string;
  event_details_url?: string;
  thank_you_url?: string;
  heading?: string;
  waitlist_copy?: string;
  submit_label?: string;
}

/**
 * Light DOM touch-up on HubSpot's rendered form: group the checkbox field(s)
 * into one shaded panel, matching the design. Pure DOM work: this subtree was
 * injected by HubSpot's own script, not rendered by React, so mutating it
 * directly here is the same pattern HubSpotForm.tsx already uses for its
 * onFormReady tweaks. No field hiding, no custom validation gate -- HubSpot's
 * own Submit and validation behave completely natively.
 */
function groupCheckboxFields(container: HTMLDivElement): void {
  const checkboxFields = Array.from(container.querySelectorAll<HTMLElement>(".hs-form-field")).filter((field) => {
    const control = field.querySelector<HTMLInputElement>("input");
    return control && CHECKBOX_FIELD_NAMES.has(control.name);
  });
  if (checkboxFields.length === 0 || !checkboxFields[0].parentElement) return;
  const panel = document.createElement("div");
  panel.className = "transform-hs-checkbox-panel";
  checkboxFields[0].parentElement.insertBefore(panel, checkboxFields[0]);
  checkboxFields.forEach((field) => panel.appendChild(field));
}

function setSubmitLabel(container: HTMLDivElement, label: string): void {
  const submit = container.querySelector<HTMLInputElement | HTMLButtonElement>(
    'input[type="submit"], button[type="submit"]',
  );
  if (!submit) return;
  if (submit instanceof HTMLInputElement) submit.value = label;
  else submit.textContent = label;
  submit.setAttribute("aria-label", label);
}

export function TransformRegister({
  portal_id = "9161697",
  form_id,
  event_date_label = "",
  venue_name = "",
  venue_address = "",
  cost_label = "No cost to attend",
  event_details_url = "/c1-transform-2026",
  thank_you_url = "/c1-transform-2026/thank-you",
  heading = "C1 Transform is at capacity",
  waitlist_copy = "You can still join the waitlist in case we receive any cancellations. We’ll notify you if a spot becomes available.",
  submit_label = "Join the waitlist",
}: TransformRegisterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const formCreated = useRef(false);

  const targetId = `hsform-transform-register-${form_id.replace(/[^a-zA-Z0-9]/g, "")}`;

  useEffect(() => {
    if (!form_id || formCreated.current || typeof window === "undefined") return;
    formCreated.current = true;

    loadHubSpotSdk().then(() => {
      if (!window.hbspt || !containerRef.current) return;
      window.hbspt.forms.create({
        region: "na1",
        portalId: portal_id,
        formId: form_id,
        target: `#${targetId}`,
        onFormReady: () => {
          if (containerRef.current) {
            groupCheckboxFields(containerRef.current);
            setSubmitLabel(containerRef.current, submit_label);
          }
        },
        onFormSubmitted: () => {
          window.location.href = thank_you_url;
        },
      });
    });
  }, [form_id, portal_id, submit_label, targetId, thank_you_url]);

  const eventLine = [event_date_label, [venue_name, venue_address].filter(Boolean).join(", "), cost_label]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <style>{HUBSPOT_FORM_CSS}</style>

      <div className="bg-white px-4 md:px-8">
        <div className="mx-auto flex max-w-screen-xl items-center justify-between py-5 lg:h-16 lg:py-0">
          <a href="/" className="flex shrink-0 items-center">
            <img src="/images/svg/c1-ai-logo-dark.svg" alt="C1" className="h-[22px] w-auto" />
          </a>
          <div className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-60">
            C1 Transform 2026
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center gap-10 px-6 py-14 sm:py-16">
        <div className="flex max-w-3xl flex-col items-center gap-4 text-center">
          <span className="chip bg-[#7353FF] text-cream">
            C1 Transform 2026
          </span>
          <h1 className="whitespace-nowrap font-display text-[clamp(22px,5.4vw,50px)] font-semibold leading-none tracking-tight text-teal-dark">
            {heading}
          </h1>
          <p className="max-w-2xl text-pretty text-[17px] leading-relaxed text-charcoal sm:text-xl">
            {waitlist_copy}
          </p>
          {eventLine && (
            <p className="text-pretty text-[17px] text-charcoal sm:text-xl lg:text-[21px]">{eventLine}</p>
          )}
        </div>

        <div className="w-full max-w-[640px] border border-[#DCDADA] bg-white p-6 sm:p-8 lg:p-10">
          <div id={targetId} ref={containerRef} className="transform-hs-form" />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-teal-dark/10 px-6 py-6 sm:px-8">
        <div className="text-xs text-gray-60">C1.ai · San Francisco, CA</div>
        <div className="flex flex-wrap gap-4">
          <a href={event_details_url} className="text-xs text-teal-dark hover:text-teal-dark-hover">
            Event details
          </a>
          <a href="/privacy-policy" className="text-xs text-teal-dark hover:text-teal-dark-hover">
            Privacy policy
          </a>
        </div>
      </div>
    </div>
  );
}

// HubSpot's form is configured with "no style" (raw HTML) in the portal, so
// none of its own theme CSS ships -- this is the entire visual layer for the
// native embed's structural class names (.hs-form-field, .hs-input, etc.).
const HUBSPOT_FORM_CSS = `
.transform-hs-form form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  align-content: start;
  font-family: var(--font-body);
}
.transform-hs-form .hs_email,
.transform-hs-form .hs_linkedin_profile_url,
.transform-hs-form .hs_are_you_a_c1_customer,
.transform-hs-form .hs_tshirt_size,
.transform-hs-form .hs_c1_transform_accessibility_requirements,
.transform-hs-form .hs_how_did_you_hear_about_c1_transform,
.transform-hs-form .hs_error_rollup,
.transform-hs-form .hs_submit,
.transform-hs-form .transform-hs-checkbox-panel {
  grid-column: 1 / -1;
}
/* .hs-richtext (the privacy-policy consent line) sits inside an unclassed
   wrapper div that's the actual grid child -- :has() targets that wrapper
   directly since grid-column on .hs-richtext itself (a grandchild) has no
   effect on grid placement. */
.transform-hs-form form > div:has(> .hs-richtext) {
  grid-column: 1 / -1;
}
/* Dietary restrictions has an "Other" dependent field (the follow-up
   "please specify" text input). HubSpot always wraps a controller field
   that has a dependent field in .hs-dependent-field, and THAT wrapper -- not
   .hs_c1_transform_dietary_restrictions itself -- is the real grid item, so
   grid-column has to target it directly (same grandchild trap as the
   richtext rule above). This also covers the "please specify" field once
   revealed, since it just fills its now-full-width parent. The controller
   field and the revealed dependent field are plain block siblings inside
   this one grid cell, so they don't get the form grid's row gap between
   them -- flex+gap here reproduces that same 18px rhythm. */
.transform-hs-form .hs-dependent-field {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.transform-hs-form .hs-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* The site-wide .hs-fieldtype-text/-phonenumber/-select rules visually hide
   labels (sr-only clip) in favor of placeholder text -- fine for a plain text
   field, but a <select>'s "Please Select" default option carries no context
   on its own. This form always shows real labels, matching the design; the
   extra .transform-hs-form class gives these enough specificity to win. */
.transform-hs-form .hs-form-field > label {
  position: static;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: normal;
  display: block;
  font-weight: 700;
  font-size: 12px;
  color: var(--color-teal-dark);
}
.transform-hs-form .hs-form-field .hs-form-required {
  color: var(--color-teal-dark);
}
.transform-hs-form .input {
  width: 100%;
}
.transform-hs-form .hs-input:not([type="checkbox"]):not([type="radio"]) {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #DCDADA;
  /* background-color, not the background shorthand -- the shorthand resets
     background-image to none, wiping out the sitewide select chevron
     (select.hs-input in app.css) that would otherwise show through here. */
  background-color: var(--color-cream);
  padding: 12px 14px;
  font-size: 15px;
  color: var(--color-charcoal);
  outline: none;
  font-family: var(--font-body);
}
.transform-hs-form select.hs-input:not([type="checkbox"]):not([type="radio"]) {
  padding-right: 2.5rem;
}
.transform-hs-form .hs-input:focus {
  border-color: var(--color-teal-dark);
}
.transform-hs-form textarea.hs-input {
  resize: vertical;
}
.transform-hs-form .hs-form-booleancheckbox-display,
.transform-hs-form .hs-form-checkbox-display {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: var(--color-charcoal);
  font-weight: 400;
  cursor: pointer;
}
.transform-hs-form .hs-form-booleancheckbox input[type="checkbox"],
.transform-hs-form .hs-form-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: var(--color-teal-dark);
}
/* flex+gap here (not just a list-style reset) so a multi-option checkbox
   group -- e.g. dietary restrictions -- gets spacing between its <li>
   options; a no-op for the single-item boolean checkbox lists elsewhere. */
.transform-hs-form .inputs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.transform-hs-form .transform-hs-checkbox-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #ECECEA;
  padding: 18px 20px;
}
.transform-hs-form .hs-error-msgs {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
}
.transform-hs-form .hs-error-msgs label {
  color: #B3261E;
  font-size: 13px;
  font-weight: 400;
}
.transform-hs-form .hs-richtext {
  font-size: 12px;
  color: var(--color-gray-60);
}
.transform-hs-form .hs-richtext p {
  margin: 0;
  line-height: 1.5;
}
.transform-hs-form .hs-richtext a {
  color: var(--color-teal-dark);
  text-decoration: underline;
}
.transform-hs-form .hs-richtext a:hover {
  color: var(--color-teal-dark-hover);
}
.transform-hs-form .hs_submit {
  margin-top: 4px;
}
.transform-hs-form .hs-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 18px;
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  background: var(--color-teal-cta);
  color: var(--color-cream);
  cursor: pointer;
}
.transform-hs-form .hs-button:hover {
  background: var(--color-teal-cta-hover);
}
`;
